import { useEffect, useState, useRef } from "react";
import MessageBubble from "./MessageBubble";
import SuggestedReply from "./SuggestedReply";
import "../../../styles/components/management/Chat/ChatWindow.css";
import InsertFieldButton from "./InsertFieldButton";

const API_BASE = process.env.REACT_APP_API_BASE;
const notificationSound = new Audio("/sounds/notification.mp3");

export default function ChatWindow({ booking, goBack }) {
  const bookingId = booking?.ID;

  const [messagesMap, setMessagesMap] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("chatMessagesMap")) || {};
    } catch {
      return {};
    }
  });

  const [message, setMessage] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasReplied, setHasReplied] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [lastSentMessageMap, setLastSentMessageMap] = useState({});

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const hasScrolledOnOpen = useRef(false);

  const messages = messagesMap[bookingId] || [];

  const saveToSession = (key, data) => {
    sessionStorage.setItem(key, JSON.stringify(data));
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const autoGrow = (el) => {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const fetchMessages = async (isInitial = false) => {
    if (!bookingId) return;

    const lastId =
      messages.length > 0 ? messages[messages.length - 1].MESSAGE_ID : null;
    const url =
      `${API_BASE}/app/API/rbmgt/chat/messages.php?bookingId=${bookingId}` +
      (lastId ? `&sinceMessageId=${lastId}` : "");

    try {
      const res = await fetch(url);
      const data = await res.json();

      if (data.success) {
        const merged = [...(messagesMap[bookingId] || []), ...data.messages];
        const unique = Array.from(
          new Map(merged.map((m) => [m.MESSAGE_ID, m])).values()
        ).sort((a, b) => a.MESSAGE_ID - b.MESSAGE_ID);

        setMessagesMap((prev) => {
          const updated = { ...prev, [bookingId]: unique };
          saveToSession("chatMessagesMap", updated);
          return updated;
        });

        if (document.visibilityState === "hidden" && data.messages.length > 0) {
          const guestMsgs = data.messages.filter((m) => m.SOURCE === "guest");
          const lastMsg = guestMsgs[guestMsgs.length - 1];
          if (lastMsg && Notification.permission === "granted") {
            const preview = (lastMsg.MESSAGE_TEXT || "New message").substring(
              0,
              100
            );
            new Notification(
              `New message from ${booking?.GUEST_NAME || "Guest"}`,
              {
                body: `#${booking?.ID} – ${booking?.PROPERTY_NAME}, ${booking?.UNIT_NAME}\n${preview}`,
              }
            );
            notificationSound.play();
          }
        }
      }
    } catch (err) {
      console.error("❌ fetchMessages error:", err);
    } finally {
      if (isInitial) {
        setInitialLoading(false);
        setTimeout(() => {
          if (!hasScrolledOnOpen.current) {
            scrollToBottom();
            hasScrolledOnOpen.current = true;
          }
        }, 100);
      }
    }
  };

  const markMessagesAsRead = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/app/API/rbmgt/chat/sendReadMessage.php?bookingId=${bookingId}`
      );
      const data = await res.json();
      if (data.success) {
        setMessagesMap((prev) => {
          const updatedMessages = (prev[bookingId] || []).map((m) =>
            m.SOURCE === "guest" ? { ...m, READ_STATUS: "1" } : m
          );
          const updatedMap = { ...prev, [bookingId]: updatedMessages };
          saveToSession("chatMessagesMap", updatedMap);
          return updatedMap;
        });
      }
    } catch (err) {
      console.error("❌ markMessagesAsRead error:", err);
    }
  };

  useEffect(() => {
    if (bookingId) {
      setInitialLoading(true);
      setHasReplied(false);
      hasScrolledOnOpen.current = false;
      fetchMessages(true);
    }
  }, [bookingId]);

  useEffect(() => {
    if (!bookingId) return;
    const interval = setInterval(() => {
      fetchMessages();
    }, 10000);
    return () => clearInterval(interval);
  }, [bookingId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchMessages();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [bookingId]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      autoGrow(textareaRef.current);
    }
  }, [message]);

  const sendMessage = async () => {
    const trimmed = message.trim();
    if (!trimmed || isSending) return;

    const lastSent = lastSentMessageMap[bookingId];
    if (lastSent === trimmed) return;

    setIsSending(true);

    try {
      const encoded = encodeURIComponent(trimmed);
      const res = await fetch(
        `${API_BASE}/app/API/rbmgt/chat/sendMessage.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify([{ bookingId, message: encoded }]),
        }
      );

      const result = await res.json();
      if (result?.[0]?.success) {
        const messageId = result[0].new?.MESSAGE_ID;
        if (messageId) {
          setMessagesMap((prev) => {
            const updatedMessages = [
              ...(prev[bookingId] || []),
              {
                MESSAGE_ID: messageId,
                BOOK_ID: bookingId,
                SOURCE: "host",
                MESSAGE_TEXT: trimmed,
                READ_STATUS: "0",
              },
            ];
            const updatedMap = { ...prev, [bookingId]: updatedMessages };
            saveToSession("chatMessagesMap", updatedMap);
            return updatedMap;
          });

          setLastSentMessageMap((prev) => ({
            ...prev,
            [bookingId]: trimmed,
          }));

          setHasReplied(true);
          setMessage("");
          fetchMessages();
        }
      }
    } catch (err) {
      console.error("❌ sendMessage error:", err);
    } finally {
      setIsSending(false);
    }
  };

  const clearChatSession = () => {
    sessionStorage.removeItem("chatMessagesMap");
    window.location.reload();
  };

  return (
    <div className="chat-window">
      <SuggestedReply
        bookingId={booking?.ID}
        booking={booking}
        onInject={(text) => setMessage(text)}
      />

      {initialLoading && (
        <div className="chat-loading-overlay">
          <div className="chat-loading-spinner" />
          <div className="chat-loading-text">Loading chat...</div>
        </div>
      )}

      <div className="chat-header">
        <button className="back-button" onClick={goBack}>
          ← Back
        </button>
        <span className="chat-title">{booking?.GUEST_NAME || "Guest"}</span>
        <button className="clear-session-button" onClick={clearChatSession}>
          Clear Chat
        </button>
      </div>

      {!initialLoading && (
        <div className="chat-messages">
          {messages.map((msg, index) => {
            const isNew =
              index === messages.length - 1 && msg.SOURCE === "host";
            return (
              <MessageBubble
                key={msg.MESSAGE_ID}
                message={msg}
                isSeen={msg.SOURCE === "guest" && msg.READ_STATUS === "1"}
                deliveryStatus={
                  msg.SOURCE === "host"
                    ? msg.READ_STATUS === "1"
                      ? "seen"
                      : "delivered"
                    : null
                }
                className={isNew ? "fade-in" : ""}
              />
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      )}

      {!hasReplied && (
        <div className="chat-mark-read">
          <button onClick={markMessagesAsRead}>Mark as read</button>
        </div>
      )}

      <div className="chat-input">
        <InsertFieldButton
          booking={booking}
          onInsert={(fieldText) => setMessage((prev) => prev + fieldText)}
        />
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type a message..."
          rows={1}
          className="chat-textarea"
        />
        <button onClick={sendMessage} disabled={isSending}>
          {isSending ? "Sending..." : "Send"}
        </button>
      </div>
    </div>
  );
}
